import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Plus, X, ReceiptText, GitBranch } from "lucide-react";
import { toast } from "sonner";

export default function POs() {
  const navigate = useNavigate();
  const [pos, setPos] = useState([]);
  const [showNew, setShowNew] = useState(false);
  const load = () => api.get("/pos").then((r) => setPos(r.data)).catch(() => {});
  useEffect(() => { load(); }, []);

  const addCO = async (e, po) => {
    e.stopPropagation();
    const amount = parseFloat(prompt("Change order amount ($):") || "0");
    const description = prompt("Change order description:") || "";
    if (!description) return;
    await api.post(`/pos/${po.id}/change-order`, { amount, description });
    toast.success("Change order added"); load();
  };

  return (
    <div className="p-5 sm:p-8 max-w-[1500px] mx-auto" data-testid="pos-page">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h1 className="font-display font-extrabold text-3xl sm:text-4xl tracking-tight text-zinc-900">Purchase Orders</h1>
          <p className="text-sm text-zinc-500 mt-1">Every PO, tracked by its WD code. Change orders append -C##.</p>
        </div>
        <button data-testid="new-po-btn" onClick={() => setShowNew(true)}
          className="flex items-center gap-2 bg-zinc-900 text-white font-semibold text-sm px-4 py-2.5 rounded-md hover:bg-zinc-800">
          <Plus className="h-4 w-4" /> New PO
        </button>
      </div>

      <div className="space-y-2" data-testid="po-list">
        {pos.length === 0 && <div className="text-sm text-zinc-400 py-8 text-center">No purchase orders yet.</div>}
        {pos.map((po) => (
          <div key={po.id} onClick={() => navigate(`/r/po/${po.id}`)} className="bg-white border border-zinc-200 shadow-sm rounded-lg p-4 hover:border-gold-dark transition-colors cursor-pointer" data-testid={`po-${po.id}`}>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <div className="flex items-center gap-3 min-w-0">
                <ReceiptText className="h-4 w-4 text-zinc-900 shrink-0" />
                <div className="min-w-0">
                  <div className="font-mono text-sm text-zinc-900">{po.wd_code}</div>
                  <div className="text-[11px] text-zinc-500">{po.vendor} · {po.terms} · {po.created_by}</div>
                </div>
              </div>
              <div className="flex items-center gap-4">
                <span className="text-zinc-900 font-display font-semibold">${Number(po.amount).toLocaleString()}</span>
                <span className="text-[10px] px-2 py-0.5 rounded-full bg-zinc-100 text-zinc-600">{po.status}</span>
                <button data-testid={`add-co-${po.id}`} onClick={(e) => addCO(e, po)} className="text-[11px] flex items-center gap-1 text-zinc-500 hover:text-zinc-900">
                  <GitBranch className="h-3.5 w-3.5" /> Change order
                </button>
              </div>
            </div>
            {(po.change_orders || []).length > 0 && (
              <div className="mt-3 pl-7 space-y-1">
                {po.change_orders.map((co, i) => (
                  <div key={i} className="flex items-center justify-between text-[11px]">
                    <span className="font-mono text-zinc-600">{co.code}</span>
                    <span className="text-zinc-500">{co.description}</span>
                    <span className="text-zinc-900">${Number(co.amount).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>

      {showNew && <NewPOModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); load(); }} />}
    </div>
  );
}

function NewPOModal({ onClose, onCreated }) {
  const [f, setF] = useState({ vendor: "", amount: "", terms: "Net 30", project_name_abbr: "", state: "", gov_or_private: "P" });
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const submit = async () => {
    if (!f.vendor) return toast.error("Vendor required");
    try {
      const r = await api.post("/pos", { ...f, amount: parseFloat(f.amount || "0") });
      toast.success(`PO created — ${r.data.wd_code}`); onCreated();
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed"); }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={onClose}>
      <div className="w-full max-w-md bg-white border border-zinc-200 rounded-xl p-6 shadow-xl" onClick={(e) => e.stopPropagation()} data-testid="new-po-modal">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-display font-bold text-xl text-zinc-900">New purchase order</h2>
          <button onClick={onClose}><X className="h-5 w-5 text-zinc-400 hover:text-zinc-900" /></button>
        </div>
        <div className="space-y-3">
          <F label="Vendor" v={f.vendor} on={set("vendor")} testid="po-vendor" />
          <div className="grid grid-cols-2 gap-3">
            <F label="Amount ($)" type="number" v={f.amount} on={set("amount")} testid="po-amount" />
            <F label="Terms" v={f.terms} on={set("terms")} testid="po-terms" />
          </div>
          <div className="grid grid-cols-3 gap-3">
            <F label="Project abbr" v={f.project_name_abbr} on={set("project_name_abbr")} testid="po-abbr" />
            <F label="State" v={f.state} on={set("state")} testid="po-state" />
            <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">Type</span>
              <select value={f.gov_or_private} onChange={set("gov_or_private")} data-testid="po-type"
                className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-2 py-2 text-sm text-zinc-900">
                <option value="P">Private</option><option value="G">Gov</option>
              </select></label>
          </div>
        </div>
        <button data-testid="submit-new-po" onClick={submit} className="w-full mt-5 bg-zinc-900 text-white font-semibold py-2.5 rounded-md hover:bg-zinc-800">
          Create PO & generate WD code
        </button>
      </div>
    </div>
  );
}
const F = ({ label, v, on, type = "text", testid }) => (
  <label className="block"><span className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</span>
    <input type={type} value={v} onChange={on} data-testid={testid}
      className="w-full mt-1 bg-white border border-zinc-300 rounded-md px-3 py-2 text-sm text-zinc-900 focus:border-gold-dark outline-none" /></label>
);
